import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PenLine, Check } from "lucide-react";

/**
 * ESignatureModal — typed-name + timestamp + client-metadata capture.
 * Props:
 *   open, onOpenChange
 *   title (default "Confirm & sign")
 *   action ("approve"|"request_changes"|"reject"|"acknowledge")
 *   onConfirm(name, comment)  -> returns Promise
 *   defaultName (usually user.name)
 */
export function ESignatureModal({
  open,
  onOpenChange,
  title = "Confirm & sign",
  action = "approve",
  onConfirm,
  defaultName = "",
  requireComment = false,
}) {
  const [name, setName] = useState(defaultName);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (open) {
      setName(defaultName);
      setComment("");
      setErr("");
      setBusy(false);
    }
  }, [open, defaultName]);

  const submit = async () => {
    setErr("");
    if (!name.trim()) return setErr("Please type your full name to sign.");
    if (requireComment && !comment.trim()) return setErr("A comment is required for this action.");
    try {
      setBusy(true);
      await onConfirm(name.trim(), comment.trim());
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const stamp = new Date().toLocaleString();

  const actionLabel = {
    approve: "Approve & Sign",
    request_changes: "Request Changes",
    reject: "Reject",
    acknowledge: "Acknowledge & Sign",
  }[action] || "Sign";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="esign-modal"
        className="glass-strong border-white/10 bg-[#0A0E1A]/90 text-white rounded-3xl sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription className="text-[#9AA4B2]">
            Type your full name to sign. Your name, timestamp, and device signature are captured immutably with SHA-256 / RSA cryptographic seals for legal compliance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {(action === "request_changes" || action === "reject" || requireComment) && (
            <div>
              <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Comment</label>
              <Textarea
                data-testid="esign-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Describe what needs to change…"
                className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white placeholder:text-[#5B6472]"
                rows={3}
              />
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Signature</label>
            <div className="mt-1 relative">
              <PenLine className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5B6472]" size={16} />
              <Input
                data-testid="esign-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Type your full name"
                className="bg-white/[0.04] border-white/10 rounded-full pl-11 pr-4 py-6 text-white placeholder:text-[#5B6472]"
              />
            </div>
            <div className="mt-2 h-16 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-center">
              <span
                className="text-2xl text-white"
                style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive" }}
                data-testid="esign-preview"
              >
                {name || "Your signature"}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-[#5B6472]" data-testid="esign-stamp">
              Signed at {stamp}
            </div>
          </div>

          {err && (
            <div className="text-sm text-rose-400" data-testid="esign-error">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-[#9AA4B2] hover:text-white hover:bg-white/[0.04] rounded-full"
              data-testid="esign-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="esign-confirm-btn"
              className="btn-primary-glow rounded-full px-6"
            >
              {busy ? "Signing…" : (<span className="flex items-center gap-1.5"><Check size={16} /> {actionLabel}</span>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
